import type { AdminApiKey, ApiKeysListResponse, CreateApiKeyResponse } from "../types/apiKeys";
import type {
  AdminServiceRequestsResponse,
  AutoFillBasicInfoResponse,
  CreateServiceData,
  CreateUserServiceData,
  DuplicateCheckResponse,
  ServiceRequestFormData,
  ServiceState,
  ServiceStateUpdate,
  ServiceStateWithInfo,
  UpdateAdminUserData,
  UpdateServiceData,
  UpdateUserServiceData,
  UserOwnedService,
  UserServiceCountResponse,
} from "../types/index.js";
import { API_RETRY_CONFIG } from "./constants";

// 開発環境では直接バックエンドにアクセス、本番環境では環境変数から取得
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? "http://localhost:8788" : "http://localhost:8787");

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // biome-ignore lint/suspicious/noExplicitAny: SWR fetcher互換性のためany必須（unknownだとSWR型推論が破綻する）
  async request<T = any>(
    endpoint: string,
    options: RequestInit = {},
    retries: number = API_RETRY_CONFIG.MAX_RETRIES,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const isDev = import.meta.env.DEV;

    const method = (options.method ?? "GET").toUpperCase();

    const config: RequestInit = {
      credentials: "include",
      headers: {
        // GET/HEAD には Content-Type を付けない。application/json を付けると
        // CORS の simple request 条件を外れて preflight(OPTIONS) が先行する
        ...(method === "GET" || method === "HEAD" ? {} : { "Content-Type": "application/json" }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorData: Record<string, unknown>;
        try {
          errorData = await response.json();
        } catch (_jsonError) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }

        // 認証エラーでリトライ残りがある場合は再試行
        if (response.status === 401 && retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, API_RETRY_CONFIG.AUTH_RETRY_DELAY_MS));
          return this.request(endpoint, options, retries - 1);
        }

        // エラーメッセージを適切に抽出（details フィールドも含める）
        let errorMessage: string;
        if (typeof errorData.error === "string") {
          errorMessage = errorData.error;
          // details フィールドがあれば追加
          if (errorData.details) {
            errorMessage += `: ${errorData.details}`;
          }
        } else if (typeof errorData.message === "string") {
          errorMessage = errorData.message;
        } else if (errorData.error && typeof errorData.error === "object") {
          errorMessage = JSON.stringify(errorData.error);
        } else {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (isDev) {
        console.error("[API Client] Request failed:", {
          url,
          method: options.method || "GET",
          error,
        });
      }

      // ネットワークエラーでリトライ残りがある場合は再試行
      if (
        retries > 0 &&
        (error instanceof TypeError || (error instanceof Error && error.message.includes("fetch")))
      ) {
        await new Promise((resolve) =>
          setTimeout(resolve, API_RETRY_CONFIG.NETWORK_RETRY_DELAY_MS),
        );
        return this.request(endpoint, options, retries - 1);
      }

      throw error;
    }
  }

  // 認証API
  // Note: 認証はBetter Auth Client (auth-client.ts) を使用
  // このAPIクライアントは認証以外のAPIリクエストに使用されます

  // ユーザー設定API（API v1）
  async getUserSettings() {
    return this.request("/api/v1/user/settings");
  }

  // プロフィール取得（API v1）
  async getUserProfile() {
    return this.request("/api/v1/user/profile");
  }

  // プロフィール更新（API v1）
  async updateUserProfile(data: { name?: string; image?: string | null }) {
    return this.request("/api/v1/user/profile", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // パスワード変更（API v1）
  async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) {
    return this.request("/api/v1/user/change-password", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // メール認証再送信（API v1）
  async resendVerificationEmail() {
    return this.request("/api/v1/user/resend-verification", {
      method: "POST",
    });
  }

  // エリア削除API（API v1）
  // サーバー側で favorite_area・deck_order・deck_titles を同じ規則で詰め、詰めた後のデッキ設定を返す
  async deleteArea(areaNumber: number): Promise<{
    message: string;
    deckSettings: {
      deckOrder: number[];
      deckTitles: Record<string, string>;
      deckUpdatedAt: number;
    };
  }> {
    return this.request(`/api/v1/user/favorite-areas/${areaNumber}`, {
      method: "DELETE",
    });
  }

  // アカウント削除API（API v1）
  async deleteAccount(password: string, confirmation: string) {
    return this.request("/api/v1/user/account", {
      method: "DELETE",
      body: JSON.stringify({ password, confirmation }),
    });
  }

  // オンボーディング完了API（API v1）
  async completeOnboarding(): Promise<{
    completedAt: number;
    defaultFavoritesInitialized?: number;
  }> {
    return this.request("/api/v1/user/onboarding-complete", {
      method: "POST",
    });
  }

  // Pro オンボーディング完了API（API v1）
  async completeProOnboarding(): Promise<{
    completedAt: number;
  }> {
    return this.request("/api/v1/user/pro-onboarding-complete", {
      method: "POST",
    });
  }

  // デッキ設定取得（API v1）
  async getDeckSettings(): Promise<{
    deckOrder: number[];
    deckTitles: Record<string, string>;
    deckUpdatedAt: number;
  }> {
    return this.request("/api/v1/user/deck-settings");
  }

  // デッキ設定更新（API v1）
  async updateDeckSettings(data: {
    deckOrder?: number[];
    deckTitles?: Record<string, string>;
  }): Promise<{
    deckOrder: number[];
    deckTitles: Record<string, string>;
    deckUpdatedAt: number;
  }> {
    return this.request("/api/v1/user/deck-settings", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // 公開API（認証不要）
  async getPublicStats() {
    return this.request("/public/stats");
  }

  // タグAPI（API v1）
  async getTags() {
    return this.request("/api/v1/tags");
  }

  // サービス申請API（API v1）
  async submitServiceRequest(data: ServiceRequestFormData) {
    return this.request("/api/v1/service-requests", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async getServiceRequests() {
    return this.request("/api/v1/service-requests");
  }

  // 重複チェックAPI
  async checkServiceDuplicate(url: string): Promise<DuplicateCheckResponse> {
    return this.request(`/api/v1/service-requests/check-duplicate?url=${encodeURIComponent(url)}`);
  }

  // ========================================
  // ユーザー独自サービスAPI v1（/api/v1/my-services）
  // ========================================

  /**
   * ユーザー独自サービス一覧を取得（API v1）
   */
  async getUserServicesV1(): Promise<{ services: UserOwnedService[] }> {
    return this.request("/api/v1/my-services");
  }

  /**
   * ユーザー独自サービスを作成（API v1）
   */
  async createUserServiceV1(data: CreateUserServiceData): Promise<{ service: UserOwnedService }> {
    return this.request("/api/v1/my-services", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * ユーザー独自サービスの詳細を取得（API v1）
   */
  async getUserServiceV1(id: string): Promise<{ service: UserOwnedService }> {
    return this.request(`/api/v1/my-services/${id}`);
  }

  /**
   * ユーザー独自サービスを更新（API v1）
   */
  async updateUserServiceV1(
    id: string,
    data: UpdateUserServiceData,
  ): Promise<{ service: UserOwnedService }> {
    return this.request(`/api/v1/my-services/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * ユーザー独自サービスを削除（API v1）
   */
  async deleteUserServiceV1(id: string): Promise<{ message: string }> {
    return this.request(`/api/v1/my-services/${id}`, {
      method: "DELETE",
    });
  }

  /**
   * ユーザー独自サービスの件数を取得（API v1）
   */
  async getUserServiceCountV1(): Promise<UserServiceCountResponse> {
    return this.request("/api/v1/my-services/count");
  }

  // ========================================
  // API v1: 統一サービス管理API
  // ========================================

  /**
   * サービス一覧を取得（API v1: 4層統合）
   * @param locale 言語設定
   * @param favoritesOnly trueの場合、お気に入りサービスのみ取得
   */
  async getServicesV1(locale: "en" | "ja" = "en", favoritesOnly = false) {
    const params = new URLSearchParams({ locale });
    if (favoritesOnly) {
      params.append("favoritesOnly", "true");
    }
    return this.request(`/api/v1/services?${params.toString()}`);
  }

  /**
   * サービス状態を取得
   * @param serviceType サービス種別（default, custom, user_owned）
   * @param serviceId サービスID
   */
  async getServiceStateV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
  ): Promise<{ state: ServiceState }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}`);
  }

  /**
   * サービス状態を更新
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param data 更新データ
   */
  async updateServiceStateV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    data: ServiceStateUpdate,
  ): Promise<{ state: ServiceState }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * サービス状態をリセット
   * @param serviceType サービス種別
   * @param serviceId サービスID
   */
  async resetServiceStateV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
  ): Promise<{ message: string }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}`, {
      method: "DELETE",
    });
  }

  /**
   * お気に入り状態をトグル
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param isFavorite 明示的なお気に入り状態（省略時はトグル）
   * @param favoriteOrder お気に入り順序
   */
  async toggleFavoriteV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    isFavorite?: boolean,
    favoriteOrder?: number | null,
  ): Promise<{ message: string; isFavorite: boolean }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/favorite`, {
      method: "PATCH",
      body: JSON.stringify({ isFavorite, favoriteOrder }),
    });
  }

  /**
   * 非表示状態を設定
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param isHidden 非表示にするかどうか
   */
  async setHiddenV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    isHidden: boolean,
  ): Promise<{ message: string; isHidden: boolean }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/hidden`, {
      method: "PATCH",
      body: JSON.stringify({ isHidden }),
    });
  }

  /**
   * ドラッグバー色を更新
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param color 色（null でデフォルトに戻す）
   */
  async updateDragBarColorV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    color: string | null,
  ): Promise<{ message: string; dragBarColor: string | null }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/drag-bar`, {
      method: "PATCH",
      body: JSON.stringify({ color }),
    });
  }

  /**
   * ユーザー独自サービスのドラッグバー色を更新（API v1）
   * user_owned サービスは user_services テーブルに保存されるため、専用APIを使用
   * @param serviceId サービスID
   * @param color 色（null でデフォルトに戻す）
   */
  async updateUserServiceDragBarColorV1(
    serviceId: string,
    color: string | null,
  ): Promise<{ message: string; dragBarColor: string | null }> {
    return this.request(`/api/v1/my-services/${serviceId}/drag-bar-color`, {
      method: "PATCH",
      body: JSON.stringify({ color }),
    });
  }

  /**
   * サービス情報オーバーライドを取得（API v1）
   * @param serviceType サービス種別（default, custom のみ）
   * @param serviceId サービスID
   */
  async getServiceInfoV1(
    serviceType: "default" | "custom",
    serviceId: string,
  ): Promise<{ info: Record<string, unknown> | null }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/info`);
  }

  /**
   * サービス情報オーバーライドを更新（API v1）
   * @param serviceType サービス種別（default, custom のみ）
   * @param serviceId サービスID
   * @param data オーバーライドデータ
   */
  async updateServiceInfoV1(
    serviceType: "default" | "custom",
    serviceId: string,
    data: {
      customName?: string | null;
      customProvider?: string | null;
      customUrl?: string | null;
      customLogoUrl?: string | null;
      customDescription?: string | null;
      customReleaseDate?: string | null;
      customDevCountry?: string | null;
      personalNotes?: string | null;
    },
  ): Promise<{ message: string }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/info`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * サービスにタグを追加（API v1）
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param tagId タグID
   */
  async addTagV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    tagId: string,
  ): Promise<{ message: string }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tagId }),
    });
  }

  /**
   * サービスからタグを削除（API v1）
   * @param serviceType サービス種別
   * @param serviceId サービスID
   * @param tagId タグID
   */
  async removeTagV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
    tagId: string,
  ): Promise<{ message: string }> {
    return this.request(`/api/v1/service-states/${serviceType}/${serviceId}/tags/${tagId}`, {
      method: "DELETE",
    });
  }

  /**
   * サービスクリックを記録（API v1）
   * @param serviceType サービス種別
   * @param serviceId サービスID
   */
  async trackClickV1(
    serviceType: "default" | "custom" | "user_owned",
    serviceId: string,
  ): Promise<void> {
    try {
      await this.request(`/api/v1/service-states/${serviceType}/${serviceId}/click`, {
        method: "POST",
      });
    } catch (error) {
      // トラッキング失敗はユーザー体験に影響しないため、ログのみ
      console.warn(`[Tracking] Failed to track click for ${serviceType}/${serviceId}:`, error);
    }
  }

  /**
   * お気に入りサービス一覧を取得（API v1）
   */
  async getFavoritesV1(): Promise<{ favorites: ServiceStateWithInfo[] }> {
    return this.request("/api/v1/service-states/favorites");
  }

  /**
   * 非表示サービス一覧を取得（API v1）
   */
  async getHiddenServicesV1(): Promise<{ hidden: ServiceStateWithInfo[] }> {
    return this.request("/api/v1/service-states/hidden");
  }

  /**
   * お気に入りを並び替え（API v1）
   * @param serviceIds サービスID配列（serviceType付き）
   * @param area エリア番号（1-5）
   */
  async reorderFavoritesV1(
    serviceIds: Array<{ id: string; serviceType: "default" | "custom" | "user_owned" }>,
    area?: number,
  ): Promise<{ message: string }> {
    return this.request("/api/v1/service-states/favorites/reorder", {
      method: "PATCH",
      body: JSON.stringify({ serviceIds, area }),
    });
  }

  // 管理者用API
  async adminGetServices() {
    return this.request("/api/admin/services");
  }

  async adminGetService(id: string) {
    return this.request(`/api/admin/services/${id}`);
  }

  async adminCreateService(data: CreateServiceData) {
    return this.request("/api/admin/services", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async adminUpdateService(id: string, data: UpdateServiceData) {
    return this.request(`/api/admin/services/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // 非推奨: 削除機能は廃止され、トグル機能に置き換えられました
  async adminDeleteService(id: string) {
    return this.request(`/api/admin/services/${id}`, {
      method: "DELETE",
    });
  }

  async adminToggleService(id: string, isActive: boolean) {
    return this.request(`/api/admin/services/${id}/toggle`, {
      method: "PATCH",
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async adminResetService(id: string) {
    return this.request(`/api/admin/services/${id}/reset`, {
      method: "PATCH",
    });
  }

  async adminAddTagToService(serviceId: string, tagId: string) {
    return this.request(`/api/admin/services/${serviceId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tagId }),
    });
  }

  async adminRemoveTagFromService(serviceId: string, tagId: string) {
    return this.request(`/api/admin/services/${serviceId}/tags/${tagId}`, {
      method: "DELETE",
    });
  }

  // 管理者用タグAPI
  async adminGetTags() {
    return this.request("/api/admin/tags");
  }

  async adminDeleteTag(tagId: string) {
    return this.request(`/api/admin/tags/${tagId}`, {
      method: "DELETE",
    });
  }

  // いいね数集計更新
  async adminUpdateFavoriteCounts() {
    return this.request("/api/admin/update-favorite-counts", {
      method: "POST",
    });
  }

  async adminAutoFillLinks(url: string, serviceName?: string) {
    return this.request("/api/admin/services/auto-fill-links", {
      method: "POST",
      body: JSON.stringify({ url, serviceName }),
    });
  }

  async adminTranslateText(text: string) {
    return this.request<{ translatedText: string; originalText: string }>("/api/admin/translate", {
      method: "POST",
      body: JSON.stringify({ text }),
    });
  }

  async adminAutoFillBasicInfo(
    serviceName: string,
    url: string,
    availableTags: { id: string; name: string }[],
  ): Promise<AutoFillBasicInfoResponse> {
    return this.request<AutoFillBasicInfoResponse>("/api/admin/services/auto-fill-basic-info", {
      method: "POST",
      body: JSON.stringify({ serviceName, url, availableTags }),
    });
  }

  // 管理者用ユーザー管理API
  async adminGetUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    role?: string;
    sortBy?: string;
    sortOrder?: string;
    // Phase 2.1: 高度なフィルタリング
    lastLoginFrom?: string;
    lastLoginTo?: string;
    totalClicksMin?: number;
    totalClicksMax?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    // Phase 2.1: 高度なフィルタリング
    if (params?.lastLoginFrom) queryParams.append("lastLoginFrom", params.lastLoginFrom);
    if (params?.lastLoginTo) queryParams.append("lastLoginTo", params.lastLoginTo);
    if (params?.totalClicksMin !== undefined)
      queryParams.append("totalClicksMin", params.totalClicksMin.toString());
    if (params?.totalClicksMax !== undefined)
      queryParams.append("totalClicksMax", params.totalClicksMax.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/api/admin/users?${queryString}` : "/api/admin/users";

    return this.request(url);
  }

  async adminGetUser(userId: string) {
    return this.request(`/api/admin/users/${userId}`);
  }

  async adminUpdateUser(userId: string, data: UpdateAdminUserData) {
    return this.request(`/api/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async adminUpdateUserStatus(userId: string, status: string) {
    return this.request(`/api/admin/users/${userId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  async adminGetUserStats() {
    return this.request("/api/admin/users/stats/overview");
  }

  // Phase 2.2: 一括操作API
  async adminBulkUpdateUserStatus(userIds: string[], status: "active" | "suspended" | "deleted") {
    return this.request("/api/admin/users/bulk/status", {
      method: "PATCH",
      body: JSON.stringify({ userIds, status }),
    });
  }

  async adminBulkDeleteUsers(userIds: string[]) {
    return this.request("/api/admin/users/bulk", {
      method: "DELETE",
      body: JSON.stringify({ userIds }),
    });
  }

  // Phase 2.3: CSVエクスポート
  async adminExportUsersCSV(params?: {
    search?: string;
    status?: string;
    role?: string;
    sortBy?: string;
    sortOrder?: string;
    lastLoginFrom?: string;
    lastLoginTo?: string;
    totalClicksMin?: number;
    totalClicksMax?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.role) queryParams.append("role", params.role);
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params?.lastLoginFrom) queryParams.append("lastLoginFrom", params.lastLoginFrom);
    if (params?.lastLoginTo) queryParams.append("lastLoginTo", params.lastLoginTo);
    if (params?.totalClicksMin !== undefined)
      queryParams.append("totalClicksMin", params.totalClicksMin.toString());
    if (params?.totalClicksMax !== undefined)
      queryParams.append("totalClicksMax", params.totalClicksMax.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/api/admin/users/export?${queryString}` : "/api/admin/users/export";

    // CSVファイルとして取得するため、通常のrequestメソッドではなくfetchを直接使用
    const response = await fetch(`${this.baseUrl}${url}`, {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`CSVエクスポートに失敗しました: ${response.statusText}`);
    }

    return response.blob();
  }

  // Phase 2.4: アクティビティログAPI
  async adminGetActivityLogs(params?: {
    page?: number;
    limit?: number;
    startDate?: number;
    endDate?: number;
    actionType?: string;
    adminUserId?: string;
    targetUserId?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.startDate) queryParams.append("startDate", params.startDate.toString());
    if (params?.endDate) queryParams.append("endDate", params.endDate.toString());
    if (params?.actionType) queryParams.append("actionType", params.actionType);
    if (params?.adminUserId) queryParams.append("adminUserId", params.adminUserId);
    if (params?.targetUserId) queryParams.append("targetUserId", params.targetUserId);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/activity-logs?${queryString}`
      : "/api/admin/activity-logs";

    return this.request(url);
  }

  async adminGetUserActivityLogs(userId: string, limit?: number) {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append("limit", limit.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/activity-logs/user/${userId}?${queryString}`
      : `/api/admin/activity-logs/user/${userId}`;

    return this.request(url);
  }

  // ========================================
  // 管理者用サービス申請API
  // ========================================

  async adminGetServiceRequests(params?: {
    page?: number;
    limit?: number;
    status?: "all" | "pending" | "approved" | "rejected";
    search?: string;
  }): Promise<AdminServiceRequestsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/api/admin/service-requests?${queryString}`
      : "/api/admin/service-requests";

    return this.request(url);
  }

  async adminGetServiceRequest(id: string) {
    return this.request(`/api/admin/service-requests/${id}`);
  }

  async adminApproveServiceRequest(id: string, adminNotes?: string): Promise<{ message: string }> {
    return this.request(`/api/admin/service-requests/${id}/approve`, {
      method: "PATCH",
      body: JSON.stringify({ adminNotes }),
    });
  }

  async adminRejectServiceRequest(id: string, adminNotes: string): Promise<{ message: string }> {
    return this.request(`/api/admin/service-requests/${id}/reject`, {
      method: "PATCH",
      body: JSON.stringify({ adminNotes }),
    });
  }

  // ========================================
  // 公開 API キー管理（ユーザー本人向け・API v1）
  // ========================================

  /** 自分の API キー一覧（prefix / name / 最終使用 / 作成 / 失効。ハッシュ・平文は含まない） */
  async getApiKeys(): Promise<ApiKeysListResponse> {
    return this.request("/api/v1/api-keys");
  }

  /** API キーを作成。平文キーはこのレスポンスでのみ返るため、直後に一度だけ表示する */
  async createApiKey(
    name: string,
    scope: "read" | "read_write" = "read",
  ): Promise<CreateApiKeyResponse> {
    return this.request("/api/v1/api-keys", {
      method: "POST",
      body: JSON.stringify({ name, scope }),
    });
  }

  /** API キーを失効（本人所有のみ） */
  async revokeApiKey(id: string): Promise<{ success: true }> {
    return this.request(`/api/v1/api-keys/${id}`, {
      method: "DELETE",
    });
  }

  // ========================================
  // 管理者用 API キー管理（全ユーザー分の閲覧・失効）
  // ========================================

  /** 全ユーザーのキー一覧（所有者メール付き） */
  async adminGetApiKeys(): Promise<{ keys: AdminApiKey[] }> {
    return this.request("/api/admin/api-keys");
  }

  /** 任意ユーザーのキーを失効 */
  async adminRevokeApiKey(id: string): Promise<{ success: true }> {
    return this.request(`/api/admin/api-keys/${id}/revoke`, {
      method: "POST",
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// SWR fetcher
export const fetcher = (url: string) => apiClient.request(url);

// SWR keys
export const SWR_KEYS = {
  // API v1 endpoints
  TAGS: "/api/v1/tags",
  USER_SETTINGS: "/api/v1/user/settings",
  USER_PROFILE: "/api/v1/user/profile",
  DECK_SETTINGS: "/api/v1/user/deck-settings",
  // 公開API keys
  PUBLIC_STATS: "/public/stats",
  PUBLIC_BANNERS: "/public/banners",
  // 公開 API: 終了したサービスのアーカイブ（認証不要、終了日降順）
  PUBLIC_DISCONTINUED: (locale: "en" | "ja" = "en") => `/public/discontinued?locale=${locale}`,
  SERVICE_REQUESTS: "/api/v1/service-requests",
  // 公開 API キー管理（ユーザー本人）
  API_KEYS: "/api/v1/api-keys",
  // Admin API keys
  ADMIN_API_KEYS: "/api/admin/api-keys",
  ADMIN_SERVICES: "/api/admin/services",
  ADMIN_TAGS: "/api/admin/tags",
  ADMIN_USERS: "/api/admin/users",
  ADMIN_USER_STATS: "/api/admin/users/stats/overview",
  ADMIN_SERVICE_REQUESTS: "/api/admin/service-requests",
  // Phase 2.4: Activity logs
  ADMIN_ACTIVITY_LOGS: "/api/admin/activity-logs",
  ADMIN_USER_ACTIVITY_LOGS: (userId: string) => `/api/admin/activity-logs/user/${userId}`,
  // API v1: サービス一覧（フルデータ）
  SERVICES_V1: (locale: "en" | "ja" = "en") => `/api/v1/services?locale=${locale}`,
  // API v1: お気に入りサービス一覧（フルデータ、FAVORITE_SERVICES_V2の代替）
  SERVICES_V1_FAVORITES: (locale: "en" | "ja" = "en") =>
    `/api/v1/services?locale=${locale}&favoritesOnly=true`,
  // API v1: 統一サービス状態管理
  SERVICE_STATE_V1: (serviceType: "default" | "custom" | "user_owned", serviceId: string) =>
    `/api/v1/service-states/${serviceType}/${serviceId}`,
  SERVICE_INFO_V1: (serviceType: "default" | "custom", serviceId: string) =>
    `/api/v1/service-states/${serviceType}/${serviceId}/info`,
  FAVORITES_V1: "/api/v1/service-states/favorites",
  HIDDEN_SERVICES_V1: "/api/v1/service-states/hidden",
  // API v1: ユーザー独自サービス
  USER_SERVICES_V1: "/api/v1/my-services",
  USER_SERVICE_V1: (id: string) => `/api/v1/my-services/${id}`,
  USER_SERVICES_COUNT_V1: "/api/v1/my-services/count",
  // API v1: デッキ公開（SNS シェア）
  PUBLISH_STATUS: "/api/v1/decks/publish/status",
  PUBLIC_DECK: (slug: string) => `/public/decks/${slug}`,
  // Admin API: バナー管理
  ADMIN_BANNERS: "/api/admin/banners",
} as const;
